export type FriendlyErrorContext =
  | 'generic'
  | 'chat-history'
  | 'chat-send'
  | 'chat-stream'
  | 'btw'
  | 'image-upload'
  | 'theme-image'
  | 'theme-request'
  | 'voice'
  | 'voice-mic'
  | 'voice-task'
  | 'invite-list'
  | 'invite-create'
  | 'invite-revoke'
  | 'clipboard'
  | 'settings-json'
  | 'template-load'
  | 'model-load'
  | 'agent-create'
  | 'agent-archive'
  | 'tool-call';

function normalizeMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error.trim();
  }

  if (error instanceof Error) {
    return error.message.trim();
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') {
      return message.trim();
    }
  }

  return '';
}

function looksLikeConnectionIssue(message: string): boolean {
  return (
    message.includes('websocket') ||
    message.includes('socket') ||
    message.includes('network') ||
    message.includes('failed to fetch') ||
    message.includes('not connected to gateway') ||
    message.includes('connection lost') ||
    message.includes('chat stream error')
  );
}

function looksLikeTooLarge(message: string): boolean {
  return (
    message.includes('413') ||
    message.includes('payload too large') ||
    message.includes('too large') ||
    message.includes('over 4 mb') ||
    message.includes('still ') && message.includes('after compression')
  );
}

function looksLikeUnsupportedFileType(message: string): boolean {
  return (
    message.includes('unsupported') ||
    message.includes('not supported') ||
    message.includes('invalid file type') ||
    message.includes('mime')
  );
}

function genericFallbackForContext(context: FriendlyErrorContext): string {
  switch (context) {
    case 'chat-history':
      return "Couldn't load this chat. Please try again.";
    case 'chat-send':
      return 'Something went wrong. Please try again.';
    case 'chat-stream':
      return 'Something went wrong. Please try again.';
    case 'btw':
      return 'Something went wrong. Please try again.';
    case 'image-upload':
    case 'theme-image':
      return "Couldn't send this image. Please try again.";
    case 'theme-request':
      return "Couldn't generate the theme. Please try again.";
    case 'voice':
      return 'Voice connection lost. Please try again.';
    case 'voice-mic':
      return "Couldn't access the microphone. Please check your mic permissions and try again.";
    case 'voice-task':
      return 'Something went wrong. Please try again.';
    case 'invite-list':
      return "Couldn't load invite links. Please try again.";
    case 'invite-create':
      return "Couldn't create the invite link. Please try again.";
    case 'invite-revoke':
      return "Couldn't revoke the invite link. Please try again.";
    case 'clipboard':
      return "Couldn't copy the link. Please try again.";
    case 'settings-json':
      return "This isn't valid JSON. Please check it and try again.";
    case 'template-load':
      return "Couldn't load templates. Please try again.";
    case 'model-load':
      return "Couldn't load models. Please try again.";
    case 'agent-create':
      return "Couldn't create the agent. Please try again.";
    case 'agent-archive':
      return "Couldn't archive the agent. Please try again.";
    case 'tool-call':
      return 'Something went wrong while the assistant was working. Please try again.';
    case 'generic':
    default:
      return 'Something went wrong. Please try again.';
  }
}

export function toFriendlyErrorMessage(
  error: unknown,
  context: FriendlyErrorContext = 'generic',
): string {
  const rawMessage = normalizeMessage(error);
  const message = rawMessage.toLowerCase();

  if (!message) {
    return genericFallbackForContext(context);
  }

  if (context === 'settings-json') {
    return genericFallbackForContext(context);
  }

  if (context === 'voice-mic') {
    return genericFallbackForContext(context);
  }

  if (looksLikeTooLarge(message)) {
    if (context === 'image-upload' || context === 'theme-image' || context === 'chat-send') {
      return 'This file is too large to send. Try a smaller image.';
    }
  }

  if (looksLikeUnsupportedFileType(message)) {
    if (context === 'image-upload' || context === 'theme-image' || context === 'chat-send') {
      return "This file type isn't supported.";
    }
  }

  if (looksLikeConnectionIssue(message)) {
    if (context === 'voice') {
      return 'Voice connection lost. Please try again.';
    }

    if (context === 'chat-send' || context === 'chat-stream') {
      return 'Connection lost. Trying to reconnect...';
    }

    if (context === 'chat-history') {
      return "Couldn't load this chat. Please try again.";
    }

    if (context === 'theme-request') {
      return 'Connection lost. Trying to reconnect...';
    }
  }

  if (
    message.includes('unauthorized') ||
    message.includes('invalid token') ||
    message.includes('pairing required') ||
    message.includes('authentication')
  ) {
    if (context === 'chat-send' || context === 'chat-stream' || context === 'theme-request') {
      return 'Connection lost. Trying to reconnect...';
    }
  }

  return rawMessage;
}
